import { BookIcon } from 'lucide-react'
import { queryClient } from '@/lib/trpc'
import { trpc, trpcClient } from '@/lib/trpc'
import { IntegrationProvider } from '../types'
import { IntegrationDTO, IntegrationType } from '@deepguide-ai/types'
import { toast } from 'sonner'
import { createOAuthConnectLifecycle, openOAuthConnectPopup } from '../oauth-connect-lifecycle'

// Create a wrapper component for the Book icon that satisfies the type system
const BookIconWrapper = () => <BookIcon className='h-5 w-5' />

/**
 * External Documentation integration provider implementation
 * This provider uses GitHub authentication to access documentation repositories
 */
const ExternalDocsProvider = {
  name: 'External Documentation',
  description:
    'Connect to documentation repositories on GitHub to help AI access your docs.',

  // Use our wrapper component for the icon
  Icon: BookIconWrapper,

  postAuthAction: 'SELECT_REPOSITORY',

  // Handler to connect to GitHub for documentation
  handleConnect: async ({
    workspaceId,
    features,
    redirectUrl,
    onSuccess,
    onFinish,
    providerName,
  }: {
    workspaceId: string
    features: string[]
    redirectUrl: string
    onSuccess: (result: any) => void
    onFinish?: (status: 'success' | 'error' | 'closed') => void
    providerName: IntegrationType
  }) => {
    const { finish } = createOAuthConnectLifecycle(onFinish)
    try {
      const data = await trpcClient.integrations.initGitHubAuth.mutate({
        workspaceId,
        features,
        // Use the central callback handler with provider parameter
        redirectUrl:
          redirectUrl ??
          `${window.location.origin}/auth-callback/${providerName}`,
        providerType: providerName,
      })

      if (!data.authUrl) {
        console.error('No auth URL returned from initialization')
        finish('error')
        return
      }

      // Open auth page in a popup window
      openOAuthConnectPopup({
        authUrl: data.authUrl, windowName: 'external-docs-auth', provider: providerName,
        onSuccess: result => {
          void queryClient.invalidateQueries({ queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey() });
          onSuccess?.(result);
        },
        onError: message => toast.error(message),
        onPopupBlocked: () => toast.error('OAuth popup was blocked. Allow popups and try again.'),
        onFinish: finish,
      })
    } catch (error: unknown) {
      console.error('Error initializing documentation auth:', error)
      toast.error(
        `Error initializing documentation authentication: ${error instanceof Error ? error.message : 'Unknown error'}`
      )
      finish('error')
    }
  },

  onConnectComplete: async ({
    code,
    state,
  }: {
    code: string
    state: string
  }) => {
    console.log('onConnectComplete', code, state)
    return await trpcClient.integrations.completeGitHubAuth.mutate({
      code,
      state,
    })
  },

  // Sync data from documentation repository
  syncData: async (integration: IntegrationDTO) => {
    try {
      // Extract features from the integration metadata
      const features = integration.metadata?.features || []

      // Sync the documentation
      await trpcClient.integrations.syncIntegration.mutate({
        integrationId: integration.id,
      })

      // Invalidate queries to refresh the UI
      queryClient.invalidateQueries({
        queryKey: trpc.integrations.getWorkspaceIntegrations.queryKey(),
      })
    } catch (error) {
      console.error('Error syncing documentation data:', error)
    }
  },
}

export const DocusaurusProvider: IntegrationProvider = {
  id: 'Docusaurus',
  name: 'Docusaurus',
  description: 'Import documentation from Docusaurus sites',
  Icon: BookIconWrapper,
  handleConnect: (params) =>
    ExternalDocsProvider.handleConnect({
      ...params,
      providerName: 'Docusaurus',
    }),
  onConnectComplete: ExternalDocsProvider.onConnectComplete,

  syncData: ExternalDocsProvider.syncData,
}

export const VitePressProvider: IntegrationProvider = {
  id: 'VitePress',
  name: 'VitePress',
  description: 'Import documentation from VitePress sites',
  Icon: BookIconWrapper,
  handleConnect: (params) =>
    ExternalDocsProvider.handleConnect({
      ...params,
      providerName: 'VitePress',
    }),
  onConnectComplete: ExternalDocsProvider.onConnectComplete,
  syncData: ExternalDocsProvider.syncData,
}

export const GitBookProvider: IntegrationProvider = {
  id: 'GitBook',
  name: 'GitBook',
  description: 'Import documentation from GitBook repositories',
  Icon: BookIconWrapper,
  handleConnect: (params) =>
    ExternalDocsProvider.handleConnect({
      ...params,
      providerName: 'GitBook',
    }),
  onConnectComplete: ExternalDocsProvider.onConnectComplete,
  syncData: ExternalDocsProvider.syncData,
}
